// Streaming chat client for Lovable AI edge function

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;

export type ChatMessageContent =
  | string
  | Array<{ type: "text"; text: string } | { type: "image_url"; image_url: { url: string } }>;

export type ChatMessage = { role: "user" | "assistant"; content: ChatMessageContent };

export interface UserContext {
  isLoggedIn: boolean;
  email?: string | null;
  phone?: string | null;
  fullName?: string | null;
}

/** Contact fields collected from the customer during the current session. */
export interface SessionContact {
  email?: string | null;
  phone?: string | null;
}

interface StreamChatOptions {
  messages: ChatMessage[];
  lang?: string;
  /** Engine id pro výběr AI vrstvy (learnings, znalosti). Např. "fasadnicke-prace", "sdk". Null = generic. */
  engine?: string | null;
  /** ID dodavatele pro tenant režim – AI použije ceník tohoto dodavatele. */
  supplierId?: string;
  /** Session ID pro ukládání konverzace (pro učení a admin). */
  sessionId?: string;
  userContext?: UserContext;
  /** Contact fields already collected from the customer in this session. */
  sessionContact?: SessionContact;
  /** AbortSignal pro zrušení streamu (např. při odchodu ze stránky nebo novém odeslání). */
  signal?: AbortSignal;
  onDelta: (text: string) => void;
  onDone: () => void;
  onError?: (error: string) => void;
  onLeadData?: (data: Record<string, unknown>) => void;
}

/**
 * Parsuje jeden SSE řádek a vrátí textový delta (nebo null).
 * Sdílená logika pro hlavní smyčku i závěrečný flush.
 */
function parseSSELine(line: string): string | null {
  if (line.endsWith("\r")) line = line.slice(0, -1);
  if (line.startsWith(":") || line.trim() === "") return null;
  if (!line.startsWith("data: ")) return null;

  const jsonStr = line.slice(6).trim();
  if (jsonStr === "[DONE]") return null;

  try {
    const parsed = JSON.parse(jsonStr);
    return (parsed.choices?.[0]?.delta?.content as string) ?? null;
  } catch {
    return null;
  }
}

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  signal?: AbortSignal,
  retries = 2
): Promise<Response> {
  for (let i = 0; i <= retries; i++) {
    try {
      const resp = await fetch(url, { ...options, signal });
      return resp;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      if (i === retries) throw e;
      await new Promise((r) => setTimeout(r, 500 * (i + 1)));
    }
  }
  throw new Error("Request failed");
}

export async function streamChat({
  messages,
  lang,
  engine,
  supplierId,
  sessionId,
  userContext,
  sessionContact,
  signal,
  onDelta,
  onDone,
  onError,
  onLeadData,
}: StreamChatOptions) {
  let resp: Response;
  try {
    resp = await fetchWithRetry(
      CHAT_URL,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ messages, lang: lang || "cs", ...(engine ? { engine } : {}), userContext, ...(supplierId ? { supplier_id: supplierId } : {}), ...(sessionId ? { session_id: sessionId } : {}), ...(sessionContact?.email ? { session_contact: sessionContact } : {}) }),
      },
      signal
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      onDone();
      return;
    }
    onError?.("Chyba při komunikaci s AI. Zkontrolujte připojení a zkuste to znovu.");
    onDone();
    return;
  }

  if (!resp.ok) {
    let errorMsg = "Chyba při komunikaci s AI.";
    try {
      const err = await resp.json();
      if (err.error) errorMsg = err.error;
    } catch {}
    onError?.(errorMsg);
    onDone();
    return;
  }

  if (!resp.body) {
    onError?.("Prázdná odpověď od AI.");
    onDone();
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let fullResponse = "";

  try {
    outer: while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        const line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        // Detekujeme [DONE] přímo, aby se přerušila vnější smyčka
        const trimmed = line.endsWith("\r") ? line.slice(0, -1).trimStart() : line.trimStart();
        if (trimmed === "data: [DONE]") break outer;

        const delta = parseSSELine(line);
        if (delta) {
          fullResponse += delta;
          onDelta(delta);
        }
      }
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      // Stream byl záměrně zrušen – tiše ukončíme
      onDone();
      return;
    }
    console.error("Stream read error:", e);
  } finally {
    reader.releaseLock();
  }

  // Závěrečný flush – zpracuje případný zbytek bufferu
  for (const raw of textBuffer.split("\n")) {
    if (!raw) continue;
    const delta = parseSSELine(raw);
    if (delta) {
      fullResponse += delta;
      onDelta(delta);
    }
  }

  // Extrakce lead dat z celé odpovědi
  const leadMatch = fullResponse.match(/---LEAD_DATA---\s*(\{[\s\S]*?\})\s*---END_LEAD---/);
  if (leadMatch) {
    try {
      const leadData = JSON.parse(leadMatch[1]);
      onLeadData?.(leadData);
    } catch (e) {
      console.error("Failed to parse lead data:", e);
    }
  }

  onDone();
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
